import { SeadevShedBuilder } from '../ssb_builder';
import { METAL_OVERHANG_GROUP_KEY, METAL_OVERHANG_KEY } from './constants';
import * as helpers from '../helpers/';

declare var jQuery: any;

/**
 * ShedRoofOverhang
 * This class handle only for the use case of OLS and Sunset clients
 * which customize overhang / without overhang for shed
 */
export class ShedRoofOverhang {
	private _builder: SeadevShedBuilder;
	private _onPriceUpdate: () => void;

	constructor(builder: SeadevShedBuilder, onPriceUpdate: () => void) {
		this._builder = builder;
		this._onPriceUpdate = onPriceUpdate;
	}

	// --- State Queries ---

	// Check whether the roof overhang toggle is currently set to "with overhang".
	isWithOverhangSelected = () => {
		return jQuery('input.ssb_roof_overhang_toggle[value="with_overhang"]').is(':checked');
	};

	// Check overhang toggle value to use in sharable link
	getRoofOverhangValue = () => {
		return jQuery('input.ssb_roof_overhang_toggle:checked').val();
	};

	// Determine whether the current shed type includes a dedicated without-overhang model variant.
	hasWithoutOverhangModelVersion = () => {
		return this._builder._shedTypeVersion.includes('wo_overhang');
	};

	// Treat the shed as without overhang only when that model version exists and the toggle is not enabled.
	isWithoutOverhang = () => {
		return this.hasWithoutOverhangModelVersion() && !this.isWithOverhangSelected();
	};

	// Check if roofType is metal or not
	isMetalRoofType = () => {
		const roofType = this._builder.getRoofType();
		const metalRoofTypes = ['metal', 'metal_horizontal'];
		return metalRoofTypes.includes(roofType ?? '');
	};

	// --- 3D / DOM ---

	/**
	 * Show or remove the roof-overhang toggle based on model support.
	 */
	toggleSectionVisibility = () => {
		const overhangToggleSection = jQuery('.group-upgrade-roof_overhang-toggle');
		const supportsOverhangToggle = this.hasWithoutOverhangModelVersion();

		if (supportsOverhangToggle) {
			overhangToggleSection.show().addClass('active');
		} else {
			overhangToggleSection.remove();
		}
	};

	/**
	 * Return the clone object naming suffix based on overhang state.
	 */
	getCloneObjectNaming = (): string => {
		return this.isWithoutOverhang() ? 'wo_clone_obj' : 'clone_obj';
	};

	// --- UI Events + Pricing ---

	/**
	 * Bind the roof-overhang toggle and apply the initial selected state.
	 */
	bindToggle = () => {
		if (!this.hasWithoutOverhangModelVersion()) {
			return;
		}

		const toggleSelector = 'input.ssb_roof_overhang_toggle';
		const selectedValue = jQuery(toggleSelector + ':checked').val();
		jQuery(document).on('change', toggleSelector, (event) => {
			this.handleToggleChange(jQuery(event.currentTarget).val());
		});

		this.handleToggleChange(selectedValue);
	};

	/**
	 * Handles toggle changes for roof overhang options.
	 *
	 * - If 'no_overhang' is selected:
	 *   Resets overhang values to default, hides related UI sections/options,
	 *   and clears any custom roof overhang settings in the builder.
	 *
	 * - If 'with_overhang' is selected:
	 *   Applies default overhang values from settings and shows related UI sections/options.
	 *
	 * Finally, refreshes the UI to reflect the changes.
	 *
	 * @param {string} selectedOption - The selected overhang option ('no_overhang' or 'with_overhang')
	 */
	handleToggleChange = (selectedOption: string) => {
		if (selectedOption === 'no_overhang') {
			this.resetDefaultOverhangValues();
			this.hideOverhangSection();
			this.hideOverhangOptions();
			this._builder.resetCustomRoofOverhang();
		} else if (selectedOption === 'with_overhang') {
			this.applyDefaultOverhangValuesFromSettings();
			this.showOverhangSection();
			this.showOverhangOptions();
		}

		this.refreshUI();
	};

	/**
	 * Hide overhang section
	 */
	hideOverhangSection = () => {
		const overhangSection = jQuery('.group-upgrade-roof_overhang');
		overhangSection.addClass('is-overhang-section-hidden');
		jQuery('input.ssb_roof_overhang').prop('checked', false);
		jQuery('#js-shed-data input[name="ssb-option-roof-overhang"]')
			.val('')
			.attr('data-name', '');
	};

	/**
	 * Show overhang section
	 */
	showOverhangSection = () => {
		const overhangSection = jQuery('.group-upgrade-roof_overhang');
		const visibleOverhangItems = overhangSection.find('.wrap-item:not(.hidden)');
		const hasSelectedOverhang = jQuery('input.ssb_roof_overhang:checked').length > 0;

		if (!hasSelectedOverhang) {
			visibleOverhangItems.first().find('input').prop('checked', true).trigger('change');
		}
		if (visibleOverhangItems.length > 1) {
			overhangSection.removeClass('is-overhang-section-hidden');
		}
	};

	/**
	 * Hide overhang upgrade options
	 */
	hideOverhangOptions = () => {
		const overhangOptionsSection = jQuery('[data-group-id="overhang_options"]');
		overhangOptionsSection.addClass('is-overhang-options-hidden');
		overhangOptionsSection.find('input').prop('checked', false);
	};

	/**
	 * Show overhang upgrade options
	 */
	showOverhangOptions = () => {
		const overhangOptionsSection = jQuery('[data-group-id="overhang_options"]');
		const hasSelectedOption = overhangOptionsSection.find('input:checked').length > 0;
		overhangOptionsSection.removeClass('is-overhang-options-hidden');
		if (!hasSelectedOption) {
			overhangOptionsSection
				.find('.wrap-item:not(.disabled):first input')
				.prop('checked', true)
				.trigger('change');
		}
	};

	/**
	 * handle for overriding upgrade option list
	 * case for Roof overhang option and toggle
	 */
	handleOverrideUpgradeOptions = () => {
		if (!this.hasWithoutOverhangModelVersion()) {
			return;
		}
		const selectedValue = jQuery('input.ssb_roof_overhang_toggle:checked').val();
		this.handleMetalRoofOverhang();

		if (selectedValue === 'no_overhang') {
			this.hideOverhangOptions();
		} else if (selectedValue === 'with_overhang') {
			this.showOverhangOptions();
		}
	};

	/**
	 * Reset layout-level default overhang values to zero
	 */
	resetDefaultOverhangValues = () => {
		this._builder._defaultOverhangFront = 0;
		this._builder._defaultOverhangBack = 0;
		this._builder._defaultOverhangLeft = 0;
		this._builder._defaultOverhangRight = 0;
	};

	/**
	 * Restore layout-level default overhang values from the current settings.
	 */
	applyDefaultOverhangValuesFromSettings = () => {
		const overhangSettings = this._builder.getOverhangSetting();

		this._builder._defaultOverhangFront = helpers.toFeetOrFallback(
			Number(overhangSettings.front),
			0,
		);
		this._builder._defaultOverhangBack = helpers.toFeetOrFallback(
			Number(overhangSettings.back),
			0,
		);
		this._builder._defaultOverhangLeft = helpers.toFeetOrFallback(
			Number(overhangSettings.left),
			0,
		);
		this._builder._defaultOverhangRight = helpers.toFeetOrFallback(
			Number(overhangSettings.right),
			0,
		);
	};

	/**
	 * Re-render roof overhang state and recalculate pricing.
	 */
	refreshUI = () => {
		this._builder.initRoof();
		this._builder.updateRoofOverhang();
		this.handleMetalRoofOverhang();
		this._onPriceUpdate();
	};

	/**
	 * Handle Update overhang metal material price only.
	 * Check/uncheck hidden radio inputs for metal roof overhang based on roof type and overhang state.
	 */
	handleMetalRoofOverhang = () => {
		const hiddenMetalRoofOverhangOptionsClass = 'is-metal-roof-overhang-options-hidden';
		const metalRoofOverhangOption = jQuery(`[data-group-id=${METAL_OVERHANG_GROUP_KEY}]`);
		metalRoofOverhangOption.addClass(hiddenMetalRoofOverhangOptionsClass);

		metalRoofOverhangOption.find('input').prop('checked', false);

		if (!this.isMetalRoofType()) {
			return;
		}

		const key = this.isWithOverhangSelected()
			? METAL_OVERHANG_KEY.WITH
			: METAL_OVERHANG_KEY.WITHOUT;

		metalRoofOverhangOption.find(`input[data-custom-key="${key}"]`).prop('checked', true);
	};
}
